import { Layout, Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import {
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
  getFirstFileLinkIfExist,
  getYearFromDateValue,
} from "utils/utils";
import { transformDpatTableRows, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";
import { isDpatIncidentYear, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const SERVICE_PROVIDER_PROGRAM = "nGFVo65uUE4";
const DOCUMENTS_HUB_PROGRAM = "SERtHvYVHRT";
const MONITORING_STAGE = "moNpY6uzqRW";
const CONTRACT_FILE = "tPYEusPtz9B";
const MONITORING_REPORT_FILE = "MjPmcbueEdU";
const SANITATION_SERVICE_PROVIDER_DOC_TYPE = "Sanitation Service Provider";
const SANITATION_PROVIDER_TYPE = "Sanitation Service Provider";

function isStartDateOnOrBeforeSelectedYear(startDateRaw, selectedYear) {
    const yearNum = Number(selectedYear);
    if (!Number.isFinite(yearNum)) {
        return false;
    }

    const startYear = getYearFromDateValue(startDateRaw);
    if (isDpatScoreRelaxationExempt()) {
        return isDpatIncidentYear(startYear, yearNum);
    }
    return startYear != null && startYear <= yearNum;
}

function buildFileUrl(eventId, dataElementId) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementId}`;
}

function resolveContractLink(providers, reports) {
  for (const provider of providers) {
    const eventId = getFirstFileLinkIfExist(
      reports,
      CONTRACT_FILE,
      provider.trackedEntity,
      MONITORING_STAGE,
    );
    if (eventId) {
      return buildFileUrl(eventId, CONTRACT_FILE);
    }
  }
  return "Not Uploaded";
}

function resolveSanitationProviderReport(documents, docReports) {
  const providerDocs = formatDataGeneral(
    documents,
    "DPAT | M&E Tool Type",
    SANITATION_SERVICE_PROVIDER_DOC_TYPE,
  ) || [];

  for (const document of providerDocs) {
    const eventId = getFileLinkIfExist(docReports, MONITORING_REPORT_FILE, document.trackedEntity);
    if (eventId) {
      return {
        frequency: getAttributeValue("DPAT | Frequency of monitoring", document),
        report: buildFileUrl(eventId, MONITORING_REPORT_FILE),
      };
    }
  }

  return { frequency: "None", report: "Not Uploaded" };
}

const SanitationServiceProviders = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [data, setData] = useState([]);
  const [monitoring, setMonitoring] = useState([{
    key: "sanitation-monitoring-summary",
    availability: "NO",
    frequency: "None",
    copyOfContract: "Not Uploaded",
    reportAvailability: "NO",
  }]);
  const [providerDocuments, setProviderDocuments] = useState([]);
  const [maxScore, setMaxScore] = useState(2);

  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI5",
      area: "Environmental Health, Sanitation and Climate Action",
      maxScore,
      data,
      monitoring,
      document: providerDocuments,
      scorei,
      scoreii,
    }),
  }));

  const DPAT_SLUG = "sdi5.0-5.1";

  const loadFromDhis2 = useCallback(() => {
    const providerRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${SERVICE_PROVIDER_PROGRAM}&pageSize=5000`,
    );
    const documentRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${DOCUMENTS_HUB_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );

    Promise.all([providerRequest, documentRequest])
      .then(([providerResult, documentResult]) => {
        const providers = providerResult.data?.instances || [];
        const documents = documentResult.data?.instances || [];
        const sanitationProviders = formatDataGeneral(providers, "DPAT | Service Provider", SANITATION_PROVIDER_TYPE) || [];
        const sanitationProvidersUpToYear = sanitationProviders.filter((provider) => (
          isStartDateOnOrBeforeSelectedYear(getAttributeValue("Start Date", provider), year)
        ));

        const providerEventsPromise = providers.length > 0
          ? axios.get(`/tracker/events?program=${SERVICE_PROVIDER_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        const documentEventsPromise = documents.length > 0
          ? axios.get(`/tracker/events?program=${DOCUMENTS_HUB_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        return Promise.all([providerEventsPromise, documentEventsPromise]).then(([providerEvents, documentEvents]) => {
          const reports = providerEvents.data?.instances || [];
          const docReports = documentEvents.data?.instances || [];
          const providerRows = sanitationProvidersUpToYear.map((provider, index) => ({
            key: provider.trackedEntity || `provider-${index}`,
            provider: getAttributeValue("Name of Business", provider),
            period: getAttributeValue("DPAT | Period of Contract", provider),
            date: getAttributeValue("Start Date", provider),
            phone: getAttributeValue("Phone", provider),
          }));

          const contractLink = resolveContractLink(sanitationProvidersUpToYear, reports);
          const sanitationReport = resolveSanitationProviderReport(documents, docReports);
          const hasMonitoringReport = sanitationReport.report !== "Not Uploaded";
          const hasContract = contractLink !== "Not Uploaded";
          const monitoringFrequency = sanitationReport.frequency !== "N/A" && sanitationReport.frequency
            ? sanitationReport.frequency
            : "None";

          const providerDocumentRows = [{
            key: "sanitation-service-provider-report",
            title: "Sanitation Service Provider Report",
            link: hasMonitoringReport ? sanitationReport.report : "Not Uploaded",
          }];

          setData(providerRows);
          setMonitoring([{
            key: "sanitation-monitoring-summary",
            availability: sanitationProvidersUpToYear.length > 0 && (hasMonitoringReport || hasContract) ? "YES" : "NO",
            frequency: monitoringFrequency,
            copyOfContract: contractLink,
            reportAvailability: hasMonitoringReport ? sanitationReport.report : "NO",
          }]);
          setProviderDocuments(providerDocumentRows);

          setScorei(sanitationProvidersUpToYear.length > 0 ? 1 : 0);
          const monitoringOrContractUploaded = isEvidenceLinkUploaded(sanitationReport.report)
            || isEvidenceLinkUploaded(contractLink);
          setScoreii(
            sanitationProvidersUpToYear.length > 0 && monitoringOrContractUploaded ? 1 : 0,
          );
        });
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.data) setData(snap.data);
      if (snap.monitoring) setMonitoring(snap.monitoring);
      if (snap.document) setProviderDocuments(snap.document);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const serviceProvidersColumn = [
    { title: "Service Provider", dataIndex: "provider", key: "provider" },
    { title: "Contract Period", dataIndex: "period", key: "period" },
    { title: "Start Date", dataIndex: "date", key: "date" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
  ];

  const serviceProvidersReportColumn = [
    { title: "Availability of sanitation M&E plan (Yes/No)", dataIndex: "availability", key: "availability" },
    { title: "Frequency of monitoring", dataIndex: "frequency", key: "frequency" },
    { title: "Copy of contract", dataIndex: "copyOfContract", key: "copyOfContract" },
    { title: "Availability of Monitoring Reports (Yes/No)", dataIndex: "reportAvailability", key: "reportAvailability" },
  ];

  const serviceProvidersListColumn = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Sanitation Service Providers", dataIndex: "link", key: "link" },
  ];

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi5.0-5.1-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            dhis2ProgramId={SERVICE_PROVIDER_PROGRAM}
            movDefaultText="Programs to register all DPAT Value Chain Service Providers in the district."
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.1i Actual Score:" score={scorei} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.1ii Actual Score:" score={scoreii} />
          </IndicatorScoreSection>

          <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of Availability of Sanitation Service Providers</Title>
          <Table
            columns={serviceProvidersColumn}
            dataSource={data || []}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>II- Evidence of Monitoring and Evaluation of Service Providers</Title>
          <Table
            columns={serviceProvidersReportColumn}
            dataSource={transformDpatTableRows(monitoring, ["copyOfContract", "reportAvailability"], {
              copyOfContract: "View Contract",
              reportAvailability: "View Report",
            })}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>III- Sanitation Service Providers Document</Title>
          <Table
            columns={serviceProvidersListColumn}
            dataSource={transformDpatTableRows(providerDocuments, ["link"], { link: "View Report" })}
            pagination={false}
            bordered
            rowKey="key"
          />
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default SanitationServiceProviders;
